import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { UserRole } from "@/mocks/admin";
import { rolePermissions } from "@/mocks/admin";
import { supabase } from "@/lib/supabase";

const ROLE_KEY = "hj_admin_role";

export interface AdminSession {
  token: string;
  role: UserRole;
  email: string;
  name: string;
  userId: string;
}

/** Supabase profiles.role values that are allowed into the admin panel */
const ALLOWED_PROFILE_ROLES = ["admin", "super_admin"];

/** Detailed login error types */
export type LoginError =
  | "invalid_credentials" // wrong email or password
  | "no_profile"          // auth user exists but no profiles row
  | "not_admin"           // profile exists but role is not admin/super_admin
  | "supabase_error"      // network / config / RLS error
  | "unknown";

export type LoginResult =
  | { success: true }
  | { success: false; error: LoginError; detail?: string };

/** Map Supabase profiles.role → internal UserRole */
function mapProfileRole(profileRole: string): UserRole {
  if (profileRole === "super_admin") return "super_admin";
  if (profileRole === "editor") return "editor";
  if (profileRole === "author") return "author";
  if (profileRole === "columnist") return "columnist";
  return "admin"; // default for "admin" role
}

/**
 * Fetch role from public.profiles for the given user id.
 * Uses .maybeSingle() — returns null if row doesn't exist.
 * Throws on network/RLS errors.
 *
 * IMPORTANT: This query is intentionally simple — it only reads the row
 * for the currently authenticated user (id = auth.uid()). It does NOT
 * reference public.profiles inside a public.profiles policy, so there
 * is zero risk of infinite recursion.
 */
async function fetchProfileRole(
  userId: string
): Promise<{ role: string; name: string | null } | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("role, full_name")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) return null;

  const row = data as { role: string; full_name: string | null };
  return { role: row.role, name: row.full_name };
}

export function useAdminAuth() {
  const navigate = useNavigate();
  const [session, setSessionState] = useState<AdminSession | null>(null);
  const [loading, setLoading] = useState(true);

  /** Restore session on mount (page refresh) */
  useEffect(() => {
    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        // If token error (e.g., expired refresh token), clean up and exit
        if (error) {
          await supabase.auth.signOut();
          localStorage.removeItem(ROLE_KEY);
          setSessionState(null);
          setLoading(false);
          return;
        }

        if (!data.session?.user) {
          setSessionState(null);
          setLoading(false);
          return;
        }

        const user = data.session.user;
        const email = user.email ?? "";

        let profile: { role: string; name: string | null } | null = null;
        try {
          profile = await fetchProfileRole(user.id);
        } catch {
          // RLS or network error — treat as not authenticated
          await supabase.auth.signOut();
          localStorage.removeItem(ROLE_KEY);
          setSessionState(null);
          setLoading(false);
          return;
        }

        if (profile && ALLOWED_PROFILE_ROLES.includes(profile.role)) {
          const role = mapProfileRole(profile.role);
          localStorage.setItem(ROLE_KEY, role);
          setSessionState({
            token: data.session.access_token,
            role,
            email,
            name: profile.name || email,
            userId: user.id,
          });
        } else {
          // Authenticated but not an admin — clear everything
          await supabase.auth.signOut();
          localStorage.removeItem(ROLE_KEY);
          setSessionState(null);
        }
      } catch {
        // Any unexpected error — clear auth state
        try {
          await supabase.auth.signOut();
        } catch {
          // ignore
        }
        localStorage.removeItem(ROLE_KEY);
        setSessionState(null);
      }

      setLoading(false);
    };

    initSession();

    // Keep listener SYNC — avoid async inside onAuthStateChange to prevent
    // race-conditions with Supabase's internal token refresh cycle.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, supaSession) => {
      if (event === "SIGNED_OUT") {
        localStorage.removeItem(ROLE_KEY);
        setSessionState(null);
      }
      // NOTE: We intentionally do NOT handle TOKEN_REFRESHED here.
      // autoRefreshToken is enabled in supabase client config.
      // Listening to TOKEN_REFRESHED and running async code inside the
      // callback can cause "Invalid Refresh Token" race conditions.
      // The active session is already refreshed by Supabase internally.
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Login flow:
   * 1. signInWithPassword
   * 2. Fetch profile by user.id (no recursive RLS)
   * 3. Check role is admin or super_admin
   * 4. Set session state
   */
  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      // ── Step 1: Supabase Auth ──────────────────────────────────────────
      let authData: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>;
      try {
        authData = await supabase.auth.signInWithPassword({ email, password });
      } catch (err) {
        return {
          success: false,
          error: "supabase_error",
          detail:
            err instanceof Error
              ? err.message
              : "Supabase connection failed. Check your internet connection.",
        };
      }

      const { data, error: authError } = authData;

      // ── Step 2: Handle auth errors ────────────────────────────────────
      if (authError) {
        const msg = authError.message.toLowerCase();

        if (
          msg.includes("invalid login") ||
          msg.includes("invalid credentials") ||
          msg.includes("wrong password") ||
          msg.includes("user not found") ||
          msg.includes("email not found")
        ) {
          return { success: false, error: "invalid_credentials" };
        }

        if (msg.includes("email not confirmed")) {
          return {
            success: false,
            error: "supabase_error",
            detail:
              'E-Mail address is not confirmed. Go to Supabase → Authentication → Users, select your user and click "Confirm email".',
          };
        }

        // Generic Supabase error — show clean message, not raw debug text
        return {
          success: false,
          error: "supabase_error",
          detail: authError.message,
        };
      }

      if (!data.session) {
        return {
          success: false,
          error: "supabase_error",
          detail: "No session returned from Supabase.",
        };
      }

      const user = data.session.user;

      // ── Step 3: Fetch profile (by user.id only — no recursive RLS) ────
      let profile: { role: string; name: string | null } | null = null;
      try {
        profile = await fetchProfileRole(user.id);
      } catch (err) {
        // Sign out so auth state is clean
        await supabase.auth.signOut();
        return {
          success: false,
          error: "supabase_error",
          detail:
            err instanceof Error
              ? err.message
              : "Could not read the profiles table. Check RLS policies.",
        };
      }

      // ── Step 4: Profile must exist ────────────────────────────────────
      if (!profile) {
        await supabase.auth.signOut();
        return { success: false, error: "no_profile" };
      }

      // ── Step 5: Role must be admin or super_admin ─────────────────────
      if (!ALLOWED_PROFILE_ROLES.includes(profile.role)) {
        await supabase.auth.signOut();
        return { success: false, error: "not_admin", detail: profile.role };
      }

      // ── Step 6: All good — persist session ───────────────────────────
      const role = mapProfileRole(profile.role);
      localStorage.setItem(ROLE_KEY, role);
      setSessionState({
        token: data.session.access_token,
        role,
        email,
        name: profile.name || email,
        userId: user.id,
      });

      return { success: true };
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore sign-out errors
    }
    localStorage.removeItem(ROLE_KEY);
    setSessionState(null);
    navigate("/admin/login");
  }, [navigate]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!session) return false;
      const perms = rolePermissions[session.role] || [];
      return perms.includes(permission);
    },
    [session]
  );

  return { session, loading, login, logout, hasPermission };
}

export function getCurrentRole(): UserRole | null {
  return localStorage.getItem(ROLE_KEY) as UserRole | null;
}

export function checkPermission(permission: string): boolean {
  const role = getCurrentRole();
  if (!role) return false;
  const perms = rolePermissions[role] || [];
  return perms.includes(permission);
}